import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "./api";
import { useAuth } from "./auth";

const FxContext = createContext(null);

const STORAGE_KEY = "mm_currency";

/**
 * Provides:
 *   currencies      — public list of enabled currencies
 *   userCurrency    — code the user wants prices in (defaults: user.preferred_currency || "EUR")
 *   setUserCurrency — switch
 *   convert(amount, fromCcy, toCcy?) — to userCurrency by default
 *   format(amount, fromCcy)          — converted + symbol + decimals
 *   reload()
 */
export function FxProvider({ children }) {
  const { user } = useAuth();
  const [currencies, setCurrencies] = useState([]);
  const [userCurrency, setUserCurrencyState] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) || "EUR";
  });

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/currencies");
      setCurrencies(data.items || []);
    } catch { /* fail-soft */ }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // When user logs in, prefer their stored preferred_currency (only on initial)
  useEffect(() => {
    if (user?.preferred_currency && !localStorage.getItem(STORAGE_KEY)) {
      setUserCurrencyState(user.preferred_currency);
    }
  }, [user?.id]);

  const setUserCurrency = useCallback((code) => {
    code = (code || "EUR").toUpperCase();
    setUserCurrencyState(code);
    localStorage.setItem(STORAGE_KEY, code);
  }, []);

  const byCode = useMemo(() => {
    const m = {};
    for (const c of currencies) m[c.code] = c;
    return m;
  }, [currencies]);

  const convert = useCallback((amount, from = "EUR", to) => {
    const dst = (to || userCurrency || "EUR").toUpperCase();
    const src = (from || "EUR").toUpperCase();
    if (!amount || src === dst) return amount || 0;
    const srcEur = byCode[src]?.rate_to_base ?? 1;
    const dstEur = byCode[dst]?.rate_to_base ?? 1;
    if (!dstEur) return amount;
    return (amount * srcEur) / dstEur;
  }, [byCode, userCurrency]);

  const format = useCallback((amount, from = "EUR", { to, native = false } = {}) => {
    const dst = native ? from : (to || userCurrency || "EUR").toUpperCase();
    const c = byCode[dst];
    const decimals = c?.decimals ?? 2;
    const sym = c?.symbol || c?.code || dst;
    const value = native ? (amount || 0) : convert(amount, from, dst);
    const rounded = (value || 0).toFixed(decimals);
    return c?.kind === "points" ? `${rounded} ${sym}` : `${sym}${rounded}`;
  }, [byCode, convert, userCurrency]);

  const value = { currencies, byCode, userCurrency, setUserCurrency, convert, format, reload };
  return <FxContext.Provider value={value}>{children}</FxContext.Provider>;
}

export function useFx() {
  const ctx = useContext(FxContext);
  if (!ctx) throw new Error("useFx must be used inside FxProvider");
  return ctx;
}
